import { getProviderFactoryForService, Injectable } from '../../helpers';

/**
 * invokable function
 * @typedef {function():Promise|Array.<string|function():Promise>} Middleware
 */

/**
 * @class MiddlewareService
 * @property {Array.<Middleware>} global
 * @property {Object.<string, Middleware>} named
 * @property {$injector} $injector
 */
export class MiddlewareService extends Injectable {

    static get $inject() {

        return [ '$injector' ];
    }

    /**
     * @param {Middleware} middleware
     * @param {*[]} params
     * @returns {Middleware}
     */
    wrapMiddleware(middleware, params) {

        if (Array.isArray(middleware)) {

            const origin = middleware[ middleware.length - 1 ];
            middleware[ middleware.length - 1 ] = (...args) => origin(...args, ...params);
        } else {

            const origin = middleware;
            middleware = (...args) => origin(...args, ...params);
            middleware.$inject = origin.$inject || [];
        }

        return middleware;
    }

    /**
     * @param {Object} state
     * @returns {Array.<Middleware>}
     * @protected
     */
    forState(state) {

        let middleware = this.global.slice();

        if (state.middleware && state.middleware.length) {

            middleware = middleware.concat(state.middleware.map((middleware) => {

                let params = [];

                if (Array.isArray(middleware)) {

                    params = middleware.slice(1);
                    middleware = middleware[ 0 ];
                }
                if (!this.named.hasOwnProperty(middleware)) {

                    throw Error(`Unknown middleware: ${middleware}`);
                }

                return params.length ? this.wrapMiddleware(this.named[ middleware ], params) : this.named[ middleware ];
            }));
        }

        return middleware;
    }

    /**
     * @param {Array.<Middleware>} middleware
     * @returns {Promise}
     * @protected
     */
    middlewarePipe(middleware) {

        return middleware.reduce((promise, next) => {

            return promise.then(() => this.$injector.invoke(next));
        }, this.$injector.invoke(middleware.shift()))
    }

    /**
     * @param {Transition} transition
     */
    handle(transition) {

        const state = transition.$to(),
            middleware = this.forState(state);

        if (!middleware.length) {

            return Promise.resolve();
        }

        return this.middlewarePipe(middleware);
    }
}

export class MiddlewareServiceProvider {

    static get $inject() {

        return [];
    }

    /**
     * @type {Function}
     */
    get $get() {

        return getProviderFactoryForService(this, MiddlewareService, 'global', 'named');
    }

    constructor() {

        /**
         * @type {Array.<Middleware>}
         * @protected
         */
        this.global = [];

        /**
         * @type {Object.<string, Middleware>}
         * @protected
         */
        this.named = {};
    }

    /**
     * @param {...Array.<Middleware>} middleware
     * @returns {MiddlewareServiceProvider}
     */
    addGlobal(...middleware) {

        this.global = this.global.concat(middleware);

        return this;
    }

    /**
     * @param {string} name
     * @param {Middleware} middleware
     * @returns {MiddlewareServiceProvider}
     */
    addNamed(name, middleware) {

        this.named[ name ] = middleware;

        return this;
    }

    /**
     * @param {Object.<string, Middleware>} map
     * @returns {MiddlewareServiceProvider}
     */
    mapNamed(map) {

        Object.keys(map).forEach((name) => {

            this.addNamed(name, map[ name ]);
        });

        return this;
    }
}


// WEBPACK FOOTER //
// ./src/middleware/services/middleware.service.js